import {
  getElementProperties,
  getElementProperty,
  getElementPropertyResolvedCount,
  getElementPropertyResolvedCountByNameOrId,
  getExistingElementProperty,
  IPropertiesOrPropertyParent,
  isElementExpressionProperty,
} from './element-property.helpers';
import {
  ElementPropertyResolvedCounts,
  ElementPropertyType,
  ElementSelectPropertyCountType,
  ElementSwitchPropertyCountType,
  IElementProperty,
  IFactoryProperty,
} from '../models/element-property.model';
import {
  ArrayOrSingle,
  OptionallyRequired,
} from '../models/type-helpers.model';
import shallowEqual, { asArray, isDefined, isOneOf } from './array.helpers';
import { isNonArrayObject, omitUndefined } from './object.helpers';
import { required } from './function.helpers';
import { OneOfElements } from '../models/project.model';
import { createElementProperty } from './element-property-factory.helpers';
import { removeSubstrings } from './string.helpers';
import { validateFiniteNumber } from '../validation/number.validation';
import { isEqual } from 'lodash';

const operators = ['>', '<', '=', '>=', '<=', '!='] as const;
type Operator = (typeof operators)[number];
type ExpressionCondition<N extends number = number> = `${Operator}${N}`;

/**
 * Map node to simplify dropdown selections
 */
type IConditionalMapNode<Value> = {
  /**
   * Forbidden properties from IConditionalFilterNode.
   * Used for TS type narrowing
   */
  conditions?: never;
  nodes?: never;

  /**
   * Property name or id
   */
  property: string;

  /**
   * Map of property counts to new nested nodes
   */
  map: {
    [propertyCount in ElementSelectPropertyCountType]?: ArrayOrSingle<
      IConditionalNode<Value>
    >;
  };
  /**
   * Default node to return if the property count is not found in the map
   */
  defaults?: ArrayOrSingle<IConditionalNode<Value>>;
};

/**
 * Helper to get original type excluding conditions or map nodes.
 */
type IValueNode<Value> = Value extends
  { conditions: never } | { property: never; map: never }
  ? never
  : Value;

/**
 * Value of a condition
 */
type IConditionValue =
  | ExpressionCondition
  | Exclude<ElementSelectPropertyCountType, ExpressionCondition>
  | ElementSwitchPropertyCountType;

/**
 * Each property name can have a single or multiple expected values (compared as OR)
 */
type IConditions = {
  /**
   * Key is the property name or id. Value should be the expected value or multiple expected values (compared as OR)
   */
  [propertyNameOrId: string]: ArrayOrSingle<IConditionValue> | undefined;
};

export interface IConditionalFilterNode<Value> {
  /**
   * Forbidden properties from IConditionalMapNode.
   * Used for TS type narrowing
   */
  property?: never;
  map?: never;
  defaults?: never;

  /**
   * Conditions that need to be met for the property to be visible.
   * Keys are property names and values what they are expected to be to allow properties to be visible
   *
   */
  conditions: IConditions;

  /**
   * Array of nodes to filter OR an additional level of conditions
   */
  nodes: IConditionalNode<Value>[];
}

/**
 * Any kind of node/value in the tree structure
 */
export type IConditionalNode<Value> =
  | IValueNode<Value>
  | IConditionalMapNode<Value>
  | IConditionalFilterNode<Value>;

/**
 * Go through a tree of conditional nodes recursively and
 * return the nodes should be visible as a flat array of nodes.
 * Should keep the order of nodes in the tree top to bottom (ignoring depth)
 * @param propertiesOrElement - The properties to check matched conditions from
 * @param nodes - The nodes to filter
 * @returns The resolved properties
 */
export const filterConditionalNodes = <Value>(
  propertiesOrElement: IPropertiesOrPropertyParent,
  ...nodes: IConditionalNode<Value>[]
): Value[] => {
  return nodes.flatMap((node) => {
    // If it is a conditional tree, continue recursively
    if (isConditionalFilterNode(node)) {
      return matchedConditions(propertiesOrElement, node.conditions)
        ? filterConditionalNodes(propertiesOrElement, ...node.nodes) // if conditions are met, continue recursively
        : []; // Stop here if conditions are not met
    }

    // If it is a conditional map, get the nodes from the map
    if (isConditionalMapNode(node)) {
      const { property, map, defaults } = node;
      const count = getElementPropertyResolvedCountByNameOrId(
        propertiesOrElement,
        property,
      );
      // Allow use to use map with boolean values (then key is 'true' or 'false')
      const key = typeof count === 'boolean' ? String(count) : count;
      const nodes =
        key !== undefined && map[key] !== undefined ? map[key] : defaults;
      return nodes
        ? filterConditionalNodes(propertiesOrElement, ...asArray(nodes))
        : [];
    }
    // Property is not a conditional tree or map, so it should be returned as it is (should be leaf node)
    return node;
  });
};

/**
 * Get a single value from conditional nodes.
 * Use this when you expect a single value to be returned
 * @param propertiesOrElement - The element to check match conditions from (via properties)
 * @param nodes - The nodes to filter
 * @param throwIfNotFound - Whether to throw an error if no value is found
 * @returns The first matching value
 */
export const getValueFromConditionalNodes = <
  Value,
  Throw extends boolean = true,
>(
  propertiesOrElement: IPropertiesOrPropertyParent,
  nodes: IConditionalNode<Value>[],
  throwIfNotFound: Throw = true as Throw,
): OptionallyRequired<Value, Throw> => {
  return required(
    filterConditionalNodes(propertiesOrElement, ...nodes)[0],
    throwIfNotFound ? 'No item was filtered from the conditional tree' : false,
  );
};

/**
 * Check if the property is a conditional property tree
 * @param node - The property to check
 * @returns True if the property is a conditional property tree, false otherwise
 */
const isConditionalFilterNode = <Value>(
  node: IConditionalNode<Value>,
): node is IConditionalFilterNode<Value> => {
  return (
    isNonArrayObject(node) &&
    'conditions' in node &&
    isNonArrayObject(node.conditions)
  );
};

const isConditionalMapNode = <Value>(
  node: IConditionalNode<Value>,
): node is IConditionalMapNode<Value> => {
  return (
    isNonArrayObject(node) &&
    'property' in node &&
    'map' in node &&
    isNonArrayObject(node.map)
  );
};

/**
 * Property names that may invalidate prior {@link ProjectChangeType.ElementCategoryEnrich}
 * rows when edited without a replacement enrich. Includes filter `conditions` keys, map
 * `property` keys, and top-level Select/Switch rows (not Expression leaves like
 * `reused_content` or conditional-only rows like `amount_scrap_based`).
 *
 * Prefer {@link propertyEditReshapesCategoryPropertyTree} when an element snapshot exists;
 * use this set as a fallback when the property tree shape is unchanged but products still
 * must be re-derived (e.g. `material` Steel → Aluminum).
 */
export const collectCategoryPropertyTemplateDriverNames = (
  properties: readonly IConditionalNode<unknown>[],
): Set<string> => {
  const names = new Set<string>();

  for (const node of properties) {
    if (
      isNonArrayObject(node) &&
      'name' in node &&
      typeof node.name === 'string' &&
      !isConditionalFilterNode(node) &&
      !isConditionalMapNode(node) &&
      'type' in node &&
      (node.type === ElementPropertyType.Select ||
        node.type === ElementPropertyType.Switch)
    ) {
      names.add(node.name);
    }
  }

  const visitConditional = (list: readonly IConditionalNode<unknown>[]) => {
    for (const node of list) {
      if (isConditionalFilterNode(node)) {
        for (const key of Object.keys(node.conditions)) names.add(key);
        visitConditional(node.nodes);
      } else if (isConditionalMapNode(node)) {
        names.add(node.property);
        for (const nested of Object.values(node.map)) {
          if (nested !== undefined) visitConditional(asArray(nested));
        }
        if (node.defaults !== undefined) {
          visitConditional(asArray(node.defaults));
        }
      }
    }
  };

  visitConditional(properties);
  return names;
};

/**
 * Check if the conditions are met for the property to be visible.
 * @param propertiesOrElement - The element to check the conditions for.
 * @param conditions - The conditions to check.
 * @returns True if the conditions are met, false otherwise.
 */
export const matchedConditions = (
  propertiesOrElement: IPropertiesOrPropertyParent,
  conditions: IConditions,
): boolean => {
  return Object.entries(omitUndefined(conditions)).every(
    ([propertyName, expectedValueOrValues]) => {
      const expectedValues = asArray(expectedValueOrValues);
      const property = getElementProperty(propertiesOrElement, propertyName);
      const count =
        isElementExpressionProperty(property) &&
        property.count?.value !== undefined
          ? property.count.value
          : getElementPropertyResolvedCount(property);

      // Exact match of a value
      if (isOneOf(expectedValues, count)) {
        return true;
      }

      if (isElementExpressionProperty(property)) {
        const expressionConditions = expectedValues.filter(
          isExpressionCondition,
        );
        return expressionConditions.every((expectedValue) => {
          return (
            isExpressionCondition(expectedValue) &&
            evaluateExpressionCondition(expectedValue, count)
          );
        });
      }

      return false;
    },
  );
};

/**
 * Check if the condition is an expression condition
 * @param condition - The condition to check
 * @returns True if the condition is an expression condition, false otherwise
 */
const isExpressionCondition = (
  condition?: ElementPropertyResolvedCounts,
): condition is ExpressionCondition =>
  typeof condition === 'string' &&
  operators.some((operator) => condition.startsWith(operator));

/**
 * Evaluate the expression condition
 * @param condition - The condition to evaluate
 * @param count - The count to evaluate the condition against
 * @returns True if the condition is met, false otherwise
 */
const evaluateExpressionCondition = (
  condition: ExpressionCondition,
  count: ElementPropertyResolvedCounts | undefined,
): boolean => {
  if (typeof count !== 'number') {
    return false;
  }
  const operator = getExpressionConditionOperator(condition);
  const value = getExpressionConditionValue(condition);
  if (operator === '=') {
    return count === value;
  }
  if (operator === '!=') {
    return count !== value;
  }
  if (operator === '>') {
    return count > value;
  }
  if (operator === '<') {
    return count < value;
  }
  if (operator === '>=') {
    return count >= value;
  }
  if (operator === '<=') {
    return count <= value;
  }
  throw new Error(`Invalid operator: ${String(operator)}`);
};

/**
 * Get the operator from the expression condition
 * @param condition - The condition to get the operator from
 * @returns The operator or throws an error if the operator is invalid
 */
const getExpressionConditionOperator = (
  condition: ExpressionCondition,
): Operator => {
  const operator = condition.replace(/[0-9,.\s]/g, '');
  if (!isOneOf(operators, operator)) {
    throw new Error(`Invalid operator: ${operator}`);
  }
  return operator;
};

/**
 * Get the value from the expression condition
 * @param condition - The condition to get the value from
 * @returns The value or throws an error if the value is invalid
 */
const getExpressionConditionValue = (condition: ExpressionCondition): number =>
  validateFiniteNumber(parseInt(removeSubstrings(condition, ...operators)));

/**
 * Get all possible values from a tree of conditional nodes.
 * @param nodes - The nodes to get the values from.
 * @returns All possible values from the nodes.
 */
export const getAllValuesFromConditionalNodes = <Value>(
  ...nodes: IConditionalNode<Value>[]
): Value[] => {
  return nodes.flatMap((node) => {
    // If it is a conditional tree, continue recursively
    if (isConditionalFilterNode(node)) {
      return getAllValuesFromConditionalNodes(...node.nodes);
    }

    // If it is a conditional map, get the nodes from the map
    if (isConditionalMapNode(node)) {
      const { map } = node;
      const children = map
        ? Object.values(map).flatMap(asArray).filter(isDefined) // Value can be an array or a single node
        : [];
      return getAllValuesFromConditionalNodes(...children);
    }
    // Property is not a conditional tree or map, so it should be returned as it is (should be leaf node)
    return node;
  });
};

/**
 * Use this to be able to reverse engineer the selections to get a certain value
 * @param value - The value to get the path to
 * @param nodes - The nodes to search the value in
 * @returns The path to the value
 */
export const getElementPropertySelectionByValue = <Value>(
  value: Value,
  ...nodes: IConditionalNode<Value>[]
) => {
  // Find the path to the value in the conditional nodes
  const findPathToValue = <Value>(
    value: Value,
    nodes: IConditionalNode<Value>[],
    selections: Record<string, IConditionValue> = {},
  ): Record<string, IConditionValue> | undefined => {
    for (const node of nodes) {
      // If it's a conditional filter node, check if the value is in its nodes
      if (isConditionalFilterNode(node)) {
        // Convert conditions to ensure they match the expected type
        const levelSelections: Record<string, IConditionValue> = {};

        // Only add defined values that can be safely cast to IConditionValue
        Object.entries(node.conditions ?? {}).forEach(([key, value]) => {
          // Conditions might have multiple values, but we only need the one
          const selectValue = asArray(value)[0];
          if (selectValue !== undefined) {
            levelSelections[key] = stringBooleanToBoolean(selectValue);
          }
        });

        const path = findPathToValue(value, node.nodes, {
          ...selections,
          ...levelSelections,
        });
        if (path) return path;
      }

      // If it's a conditional map node, check all possible values in the map
      else if (isConditionalMapNode(node)) {
        const { property, map } = node;
        for (const [key, mapNodes] of Object.entries(map)) {
          const mapNodesArray = asArray(mapNodes).filter(isDefined);
          const path = findPathToValue(value, mapNodesArray, {
            ...selections,
            [property]: stringBooleanToBoolean(key),
          });
          if (path) return path;
        }
      }

      // If it's a leaf node and matches the value, return the current path
      else if (node !== undefined && isEqual(node, value)) {
        return selections;
      }
    }
    return undefined;
  };

  // Get the element properties that would need to be set to get the specified value
  return findPathToValue(value, nodes);
};

const stringBooleanToBoolean = <T>(value: T): T | boolean => {
  if (value === 'true' || value === 'false') {
    return value === 'true';
  }
  return value;
};

/**
 * Filter conditional properties from a list of properties.
 * Since properties can be added depending on other properties we solve this by running the filter again to check if that triggered any changes
 * Probably not the best solution bu rewriting filterConditionalNodes got even uglier
 * @param element
 * @param properties
 * @returns
 */
export const filterConditionalProperties = (
  element: OneOfElements,
  ...properties: IConditionalNode<IFactoryProperty>[]
) => {
  let currentProperties = getElementProperties(element);
  let filteredProperties: IElementProperty[] = [];

  // Loop max 3 times to make sure we don't get infinite loop (should not happen though)
  for (let i = 0; i < 3; i++) {
    filteredProperties = filterConditionalNodes<IFactoryProperty>(
      currentProperties,
      ...properties,
    )
      // skipValidation: this helper runs inside the enrich pipeline; counts may not yet match
      // freshly resolved options (e.g. category-changed property on a proposal). User-input
      // boundary still validates.
      .map((p) =>
        createElementProperty(p, undefined, undefined, {
          skipValidation: true,
        }),
      )
      .map((p) => getExistingElementProperty(currentProperties, p, false) ?? p); // If the property already exists, reuse the existing one

    // If no changes were made in this loop we can break the loop and return the filtered properties
    if (shallowEqual(currentProperties, filteredProperties)) {
      return filteredProperties;
    }

    // Run the filter again with the new properties
    currentProperties = filteredProperties;
  }

  return filteredProperties;
};
